import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getHostTrips, deleteTrip } from '../../../services/firestore/trips';
import { useAuth } from '../../../contexts/AuthContext';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { Plus, MapPin, Calendar, Edit, ArrowRight, Users, Image as ImageIcon, Star, Trash2, ExternalLink } from 'lucide-react';

// Amenity labels mapping
const AMENITY_LABELS = {
  wifi: 'WiFi',
  parking: 'Free Parking',
  kitchen: 'Kitchen',
  beach: 'Beach Access',
  mountain: 'Mountain View',
  nature: 'Nature Access',
  coffee: 'Coffee/Tea',
  tv: 'TV/Entertainment',
  airConditioning: 'Air Conditioning',
};

const HostTrips = () => {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, trip: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const loadTrips = async () => {
      if (!user) return;
      try {
        const data = await getHostTrips(user.uid);
        setTrips(data);
      } catch (error) {
        console.error('Error loading trips:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, [user]);

  const handleDeleteClick = (e, trip) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModal({ isOpen: true, trip });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.trip) return;
    
    setDeleting(true);
    try {
      await deleteTrip(deleteModal.trip.id);
      setTrips(trips.filter(t => t.id !== deleteModal.trip.id));
      setDeleteModal({ isOpen: false, trip: null });
    } catch (error) {
      console.error('Error deleting trip:', error);
      alert('Failed to delete listing. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClose = () => {
    if (!deleting) {
      setDeleteModal({ isOpen: false, trip: null });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Listing"
        message="Are you sure you want to delete this listing? This will permanently remove it and all associated data."
        itemName={deleteModal.trip?.title}
        isDeleting={deleting}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-light text-gray-900 mb-2">My Listings</h1>
            <p className="text-lg text-gray-600 font-light">
              Manage your trip listings and experiences
            </p>
          </div>
          <Link
            to="/host/trips/create"
            className="inline-flex items-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            Create Listing
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {trips.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No listings yet</h3>
            <p className="text-gray-600 mb-6">Create your first trip listing to start welcoming guests.</p>
            <Link
              to="/host/trips/create"
              className="inline-flex items-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              Create Your First Listing
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trips.map((trip) => {
              const mainImage = trip.photos && trip.photos.length > 0 ? trip.photos[0] : null;
              const additionalPhotosCount = trip.photos && trip.photos.length > 1 ? trip.photos.length - 1 : 0;
              const highlights = trip.highlights ? trip.highlights.split('\n').filter(h => h.trim()).slice(0, 3) : [];
              
              return (
                <Link
                  key={trip.id}
                  to={`/host/trips/${trip.id}/edit`}
                  className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden hover:shadow-xl hover:border-emerald-200 transition-all duration-300 flex flex-col"
                >
                  {/* Image Section */}
                  <div className="relative h-64 bg-gradient-to-br from-emerald-100 to-green-100 overflow-hidden">
                    {mainImage ? (
                      <>
                        <img
                          src={mainImage}
                          alt={trip.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {additionalPhotosCount > 0 && (
                          <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            +{additionalPhotosCount}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-emerald-600/30" />
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 left-3">
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm ${
                          trip.status === 'published'
                            ? 'bg-emerald-600/90 text-white'
                            : trip.status === 'draft'
                            ? 'bg-amber-500/90 text-white'
                            : 'bg-gray-600/90 text-white'
                        }`}
                      >
                        {trip.status === 'published' ? 'Published' : trip.status === 'draft' ? 'Draft' : trip.status}
                      </span>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-medium text-gray-900 group-hover:text-emerald-700 transition-colors line-clamp-2 mb-3">
                      {trip.title}
                    </h3>
                    
                    {/* Location and Dates */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-sm">{trip.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-sm">
                          {new Date(trip.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
                          {new Date(trip.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-sm">{trip.capacity} {trip.capacity === 1 ? 'guest' : 'guests'}</span>
                      </div>
                    </div>

                    {/* Description Preview */}
                    {trip.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-4 flex-1">
                        {trip.description}
                      </p>
                    )}

                    {/* Highlights */}
                    {highlights.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {highlights.map((highlight, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium"
                            >
                              <Star className="w-3 h-3 fill-emerald-600" />
                              {highlight.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Amenities Preview */}
                    {trip.amenities && trip.amenities.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {trip.amenities.slice(0, 3).map((amenityId) => (
                            <span
                              key={amenityId}
                              className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-medium"
                            >
                              {AMENITY_LABELS[amenityId] || amenityId}
                            </span>
                          ))}
                          {trip.amenities.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">
                              +{trip.amenities.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-auto">
                      <div>
                        <span className="text-2xl font-bold text-emerald-700">
                          ${trip.price}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">per person</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`/trips/${trip.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View as client"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={(e) => handleDeleteClick(e, trip)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete listing"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 group-hover:text-emerald-800 transition-colors">
                          Edit
                          <Edit className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default HostTrips;

